import type { Course, Classroom, LearningActivity, Provider, Location } from '@/api/generated';
import { LOCATION_TYPES } from '@/components/classForm/ClassForm.utils';
import { getDuration, getDateTimeData } from '@/utils';

import messages from './index.messages';

import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

export interface ClassOverviewProps {
    isLoading?: boolean;
    course: Course;
    classroom: Classroom;
    instructors: string[];
    provider: Provider & { isPartner: boolean };
    learningActivity?: LearningActivity;
}

export interface ProcessedClassOverviewData {
    courseTitle: string;
    courseVersion: string;
    courseLanguage: string;
    dateTimeRange: string;
    duration: string;
    timezone: string;
    deliveryType: ReactNode;
    classCapacity: number;
    instructors: string[];
    trainingProvider: ClassOverviewProps['provider'];
    customers: ReactNode;
    activityStatus?: string | null;
    lmsType?: string | null;
}

interface DateTimeData {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
}

export const formatDeliveryType = (
    formatMessage: IntlShape['formatMessage'],
    locationType: string,
    physicalAddress: Location['physicalAddress'],
): ReactNode => {
    if (locationType === LOCATION_TYPES.virtual) {
        return formatMessage(messages.virtualDelivery);
    }

    if (physicalAddress) {
        const { addressLine1, addressLine2, city, state, postalCode, country } = physicalAddress;
        return (
            <>
                <div>{addressLine1}</div>
                <div>{addressLine2}</div>
                <>
                    {city}, {state} {postalCode}, {country}
                </>
            </>
        );
    }

    return formatMessage(messages.inPersonDelivery);
};

export const formatCustomerDisplay = (
    formatMessage: IntlShape['formatMessage'],
    learningActivity?: LearningActivity,
): ReactNode => {
    if (!learningActivity?.customers?.length) return null;

    // Check activity_audience for "Semi-Private"
    if (learningActivity.activity_audience?.toLowerCase().includes('semi-private')) {
        return formatMessage(messages.semiPrivateCustomer);
    }

    return learningActivity.customers.map((customer) => (
        <div key={customer!.customer_name}>{customer!.customer_name}</div>
    ));
};

export const formatDateTimeRange = (startsOn: number, endsOn: number, timezone: string) => {
    const dateTimeResult = getDateTimeData({
        startsOn: startsOn,
        endsOn: endsOn,
        timezone: timezone,
        dateFormat: 'YYYY/MM/DD hh:mm A',
    }) as DateTimeData;

    return `${dateTimeResult.startDate} - ${dateTimeResult.endDate}`;
};

export const formatDuration = (startsOn: number, endsOn: number, locale: string): string => {
    return getDuration(startsOn, endsOn, locale);
};

export const processClassOverviewData = (
    props: ClassOverviewProps,
    formatMessage: IntlShape['formatMessage'],
    locale: string,
): ProcessedClassOverviewData => {
    const { course, classroom, instructors, provider, learningActivity = {} } = props;

    const { locationData } = classroom;
    const { startsOn, endsOn, classCapacity } = classroom;

    const { locationType, timezone, physicalAddress } = locationData!;

    const courseVersion = course.courseId.split(':').pop() as string;
    const cleanCourseVersion = courseVersion.split('-')[0];

    const dateTimeRange = formatDateTimeRange(startsOn, endsOn, timezone);
    const duration = formatDuration(Number(startsOn), Number(endsOn), locale);

    const deliveryType = formatDeliveryType(formatMessage, locationType, physicalAddress);

    const customers = formatCustomerDisplay(formatMessage, learningActivity);

    const activityStatus = learningActivity?.activity_status;
    const lmsType = learningActivity?.lms_type;

    return {
        courseTitle: course.title,
        courseVersion: cleanCourseVersion,
        courseLanguage: course.langLocale,
        dateTimeRange,
        duration,
        timezone,
        deliveryType,
        classCapacity: classCapacity!,
        instructors,
        trainingProvider: provider,
        customers,
        activityStatus,
        lmsType,
    };
};
